import { ReactNode } from "react";
import { Provider as RollbarProvider, ErrorBoundary } from "@rollbar/react";
import { getEnvVar } from "@/utils/util";

const appEnv = getEnvVar("APP_ENV", true);
const isActive = appEnv === "prod" || appEnv === "stg";

/**
 * Where a browser extension's injected script is served from.
 *
 * Extensions (MetaMask, password managers, ad blockers, translators) inject
 * scripts into the page and they run in the page's own JS context — so
 * `captureUncaught` below sees their failures through window.onerror exactly
 * as if we had thrown them, and reports them under our token and environment.
 *
 * The one that prompted this: "Failed to connect to MetaMask" from
 * chrome-extension://nkbihfbeogaeaoehlefnkodbefgpgknn/scripts/inpage.js, which
 * MetaMask throws when its injected script loses its port to the extension —
 * typically because the extension updated or was disabled while a tab was
 * open. Nothing in this app touches window.ethereum, so it is never ours.
 */
const EXTENSION_SOURCE =
  /^(chrome|chrome-untrusted|moz|safari-web|safari|ms-browser)-extension:\/\//;

/** Stack frames on a Rollbar item, from either shape it can carry. */
const framesOf = (item: Record<string, unknown>): { filename?: string }[] => {
  const body = (item?.body ?? {}) as {
    trace?: { frames?: { filename?: string }[] };
    // Chained errors (an `Error` with a `cause`) arrive as trace_chain instead.
    trace_chain?: { frames?: { filename?: string }[] }[];
  };
  if (body.trace?.frames) return body.trace.frames;
  if (Array.isArray(body.trace_chain)) {
    return body.trace_chain.flatMap((trace) => trace?.frames ?? []);
  }
  return [];
};

/**
 * Drop anything thrown *entirely* inside an extension, keep everything else.
 *
 * `every`, not `some`: an extension that wraps a page API (fetch, XHR) can
 * leave its own frame in the stack of an error our code threw, and dropping on
 * any extension frame would silently discard a real failure of ours. Requiring
 * every frame to be extension-sourced has no such false positive, and still
 * catches the case this exists for — an error thrown wholly within an injected
 * script, which carries no app frames at all.
 *
 * Deliberately NOT `hostSafeList`: that reports only errors whose frames match
 * our own hosts, which also discards every item that arrives with no usable
 * stack — the cross-origin "Script error." case — and those can be real
 * failures in our own bundle. Matching the extension protocols instead means
 * anything unrecognised is still reported.
 */
const isFromBrowserExtension = (item: Record<string, unknown>): boolean => {
  const frames = framesOf(item);
  // No frames is not evidence of an extension — that item is reported.
  return frames.length > 0 && frames.every((frame) => EXTENSION_SOURCE.test(frame?.filename ?? ""));
};

interface Props {
  children: ReactNode;
}

// Always render RollbarProvider so useRollbar() works throughout the tree.
// Rollbar is enabled in production and staging; disabled locally/dev to suppress noise.
export const CustomRollbarProvider = ({ children }: Props) => {
  return (
    <RollbarProvider
      config={{
        accessToken: getEnvVar("ROLLBAR_ACCESS_TOKEN", true),
        captureUncaught: true,
        captureUnhandledRejections: true,
        environment: appEnv || "local",
        enabled: isActive,
        // Return true to discard the item. Extension noise only — every other
        // uncaught error still reports exactly as before.
        checkIgnore: (_isUncaught, _args, item) => isFromBrowserExtension(item),
        ignoredMessages: [
          // Belt-and-braces for the extension errors above arriving with no
          // stack to match on. Safe to match by text because this app has no
          // wallet integration: a MetaMask message here can only be an
          // extension's.
          "Failed to connect to MetaMask",
          "MetaMask extension not found",
          // Benign browser notices, not failures: Chrome/Firefox raise these
          // when a ResizeObserver callback changes layout within the same
          // frame, and simply deliver the pending notifications next frame.
          // Nothing breaks, but window.onerror sees them with our page as the
          // "source", so they arrived as fresh items on every navigation.
          "ResizeObserver loop completed with undelivered notifications",
          "ResizeObserver loop limit exceeded",
        ],
      }}
    >
      <ErrorBoundary>{children}</ErrorBoundary>
    </RollbarProvider>
  );
};
